import MarketingLayout from '../../components/marketing/MarketingLayout';
import { Card, Title, Text } from "@tremor/react";
import { useState } from 'react';
import BookingWidget from '../../components/BookingWidget';
import ServicePageNavigation from '../../components/ServicePageNavigation';
import { colors } from '../../lib/colors';

export default function TermLifeInsurance() {
  const [showBookingWidget, setShowBookingWidget] = useState(false);

  return (
    <MarketingLayout>
      <section className="py-16 px-4 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto relative">
          {/* Service Navigation */}
          <ServicePageNavigation currentSlug="term-life-insurance" />
          {/* Header */}
          <div className="text-center mb-12 pt-4"> {/* Added padding top */}
            <Title className="text-4xl font-bold text-gray-800 dark:text-white mb-4">Term Life Insurance</Title>
            <Text className="text-xl text-gray-600 dark:text-gray-400">
              Affordable Protection for a Defined Period
            </Text>
          </div>

          <div className="space-y-8">
            {/* What is Term Life Insurance? */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">What is Term Life Insurance?</Title>
              <Text className="text-gray-600 dark:text-gray-300">
                Term life insurance provides life insurance coverage for a specific period, known as the "term" (e.g., 10, 20, or 30 years). If the insured person passes away during this term, the policy pays out a tax-free death benefit to the named beneficiaries.
              </Text>
              <Text className="text-gray-600 dark:text-gray-300 mt-2">
                It's generally the most affordable type of life insurance, designed to cover temporary needs like replacing income during working years, paying off a mortgage, or funding children's education if a primary earner dies prematurely. Unlike permanent insurance, term policies typically do not build cash value.
              </Text>
            </Card>

            {/* How It Works */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">How It Works</Title>
              <ol className="list-decimal list-inside text-gray-600 dark:text-gray-300 space-y-2 mt-4">
                <li>
                  <strong>Choose Term & Coverage:</strong> You select the length of the term (e.g., 10, 20, 30 years) and the amount of the death benefit needed.
                </li>
                <li>
                  <strong>Pay Premiums:</strong> You pay regular premiums (monthly or annually) which are typically guaranteed to remain level for the chosen term.
                </li>
                <li>
                  <strong>Coverage Period:</strong> The policy provides coverage for the duration of the term.
                </li>
                <li>
                  <strong>Death Benefit Payout:</strong> If the insured passes away during the term, the beneficiaries receive the death benefit amount, tax-free.
                </li>
                <li>
                  <strong>End of Term:</strong> If the insured outlives the term, the coverage expires. There is no payout, and no cash value is accumulated. Many policies offer options to renew (at higher rates) or convert to a permanent policy before expiry.
                </li>
              </ol>
            </Card>

            {/* Key Benefits */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Key Benefits</Title>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Affordability:</strong> Offers the highest amount of coverage for the lowest initial premium cost compared to permanent insurance.</li>
                <li><strong>Simplicity:</strong> Easy to understand – provides pure death benefit protection for a set period.</li>
                <li><strong>Flexibility:</strong> Covers specific needs for a defined timeframe (e.g., until mortgage is paid off or children are independent).</li>
                <li><strong>Convertibility:</strong> Many term policies allow conversion to a permanent policy later without further medical underwriting, offering future flexibility.</li>
                <li><strong>Large Coverage Amounts:</strong> Enables individuals to secure substantial coverage amounts needed during high-responsibility years.</li>
              </ul>
            </Card>

            {/* Considerations */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Considerations</Title>
              <Text className="text-gray-600 dark:text-gray-300 mb-3">Important factors to keep in mind:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Temporary Coverage:</strong> Protection expires at the end of the term unless renewed or converted.</li>
                <li><strong>No Cash Value:</strong> Term policies do not accumulate cash value like permanent policies.</li>
                <li><strong>Renewal Premiums:</strong> If renewed after the initial term, premiums will increase significantly based on the insured's attained age.</li>
                <li><strong>Conversion Deadlines:</strong> Conversion options usually have deadlines and may cease at a certain age.</li>
              </ul>
            </Card>

            {/* Suitability */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Is Term Life Insurance Right for You?</Title>
              <Text className="text-gray-600 dark:text-gray-300 mb-3">Term life insurance is often ideal for:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li>Individuals and families needing large amounts of coverage for temporary needs (e.g., mortgage protection, income replacement, debt coverage).</li>
                <li>Younger individuals seeking affordable protection.</li>
                <li>Business owners needing coverage for key persons or to secure loans for a specific period.</li>
                <li>Those prioritizing maximum coverage for the lowest cost during specific life stages.</li>
              </ul>
            </Card>

            {/* Call to Action */}
            <div className="text-center mt-12">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-4">Secure Affordable Protection Today</Title>
              <Text className="text-gray-600 dark:text-gray-400 mb-6 max-w-xl mx-auto">
                Let's determine the right term length and coverage amount to protect your family or business during critical years.
              </Text>
              <button
                onClick={() => setShowBookingWidget(true)}
                className="px-8 py-3 rounded-md text-lg font-semibold text-white transition-transform duration-150 ease-in-out hover:scale-105 active:scale-95"
                style={{ background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)` }}
              >
                Get a Term Life Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Widget Modal */}
      {showBookingWidget && (
        <BookingWidget
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
        />
      )}
    </MarketingLayout>
  );
}
